/**
 * PWA helpers — service worker registration, install prompt capture, push subs.
 */

let deferredPrompt = null;

const noop = () => {};

export const isStandalone = () =>
  (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
  || window.navigator.standalone === true;

export const isIOS = () =>
  /iPad|iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream;

export const isAndroid = () =>
  /Android/i.test(window.navigator.userAgent);

/**
 * Register the service worker once and capture Android's beforeinstallprompt.
 * Call as early as possible — from index.js or App.js mount.
 *
 * `updateViaCache: 'none'` + explicit `.update()` on every load means the
 * browser always network-checks sw.js on visit, so after a deploy users
 * migrate to the new SW within one navigation. This is what unblocks the
 * "blank page after link click" regression we hit with the old v3 SW that
 * used cache-first for HTML.
 */
export const registerPWA = ({ onInstallable = noop } = {}) => {
  if (typeof window === 'undefined') return;

  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker
        .register('/sw.js', { scope: '/', updateViaCache: 'none' })
        .then((reg) => {
          // Force a fresh check for a new SW on every page load.
          try { reg.update(); } catch (_e) { /* noop */ }

          // When a new worker takes over the page, reload once so the
          // running tab gets the latest bundle. Guard against reload
          // loops with a session flag.
          navigator.serviceWorker.addEventListener('controllerchange', () => {
            if (window.sessionStorage.getItem('lh_sw_reloaded') === '1') return;
            window.sessionStorage.setItem('lh_sw_reloaded', '1');
            window.location.reload();
          });

          // If a new SW is already waiting when we register (user came
          // back to a tab that had installed but not activated the new
          // worker), tell it to take over immediately.
          if (reg.waiting) {
            reg.waiting.postMessage({ type: 'SKIP_WAITING' });
          }
          reg.addEventListener('updatefound', () => {
            const nw = reg.installing;
            if (!nw) return;
            nw.addEventListener('statechange', () => {
              if (nw.state === 'installed' && navigator.serviceWorker.controller) {
                // An old SW is still in charge and the new one is ready —
                // hand over immediately.
                nw.postMessage({ type: 'SKIP_WAITING' });
              }
            });
          });
        })
        .catch(() => { /* swallow — non-critical */ });
    });
  }

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    try { onInstallable(); } catch (_e) { /* ignore */ }
  });
};

/**
 * Trigger the native install dialog (Android/Chrome only).
 * Returns 'accepted' | 'dismissed' | 'unavailable'.
 */
export const promptInstall = async () => {
  if (!deferredPrompt) return 'unavailable';
  deferredPrompt.prompt();
  const choice = await deferredPrompt.userChoice;
  deferredPrompt = null;
  return choice?.outcome || 'dismissed';
};

export const canPromptInstall = () => !!deferredPrompt;

/* ----------------------- Web Push subscription ------------------------ */

const urlBase64ToUint8 = (b64) => {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

/**
 * Ask the user for notification permission + subscribe to push.
 * Backend supplies a VAPID public key via GET /api/push/vapid-key.
 * Subscription is POSTed to /api/push/subscribe.
 */
export const enablePushNotifications = async () => {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { ok: false, reason: 'unsupported' };
  }
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, reason: permission };

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const api = process.env.REACT_APP_BACKEND_URL;
    const r = await fetch(`${api}/api/push/vapid-key`);
    if (!r.ok) return { ok: false, reason: 'no_vapid' };
    const { public_key } = await r.json();
    if (!public_key) return { ok: false, reason: 'no_vapid' };
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8(public_key),
    });
  }

  const token = localStorage.getItem('token');
  if (!token) return { ok: false, reason: 'not_authenticated' };
  const api = process.env.REACT_APP_BACKEND_URL;
  await fetch(`${api}/api/push/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ subscription: sub.toJSON() }),
  });
  return { ok: true };
};

export const disablePushNotifications = async () => {
  if (!('serviceWorker' in navigator)) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    try { await sub.unsubscribe(); } catch (_e) { /* ignore */ }
  }
};
